import {
  Entity,
  EntityManager,
  EntityState,
  EntityType,
  MetadataStore,
} from 'breeze-client';
import { Observable } from 'rxjs';

export interface EntityCtor<T extends Entity> {
  readonly prototype: Entity;
  new (): T;
}

export type EntityTypeLike<T extends Entity> =
  | string
  | EntityType
  | EntityCtor<T>;

export interface EntitySelectorConfig {
  // If true, the emission contain deleted entities as well
  includeDeleted?: boolean;
  // If true, emits when entities are modified, otherwise only when added or removed
  emitOnModify?: boolean;
}

export interface EntitySelector {
  getEntities$<T extends Entity>(
    entityTypeLike?: EntityTypeLike<T>,
    config?: EntitySelectorConfig
  ): Observable<T[]>;
}

export const getEntities = <T extends Entity>(
  manager: EntityManager,
  entityTypeLike?: EntityTypeLike<T>,
  includeDeleted?: boolean
): T[] => {
  let entityStates = [
    EntityState.Added,
    EntityState.Modified,
    EntityState.Unchanged,
  ];
  if (includeDeleted) {
    entityStates = [...entityStates, EntityState.Deleted];
  }
  if (!entityTypeLike) {
    return manager.getEntities(null, entityStates) as T[];
  }

  const entityType = getEntityType(entityTypeLike, manager.metadataStore);
  return manager.getEntities(
    entityType.getSelfAndSubtypes(),
    entityStates
  ) as T[];
};

export const getEntityType = <T extends Entity>(
  entityTypeLike: EntityTypeLike<T>,
  metadataStore: MetadataStore,
  okIfNotFound?: boolean
): EntityType => {
  if (typeof entityTypeLike === 'string') {
    entityTypeLike = metadataStore.getAsEntityType(
      entityTypeLike,
      okIfNotFound
    );
  } else if (!(entityTypeLike instanceof EntityType)) {
    entityTypeLike = entityTypeLike.prototype.entityType;
  }
  return entityTypeLike;
};
